import { useState } from "react";
import { Button, ErrorPanel, Skeleton } from "@/components/atoms";
import { EmptyState } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useGetVehiclesQuery } from "@/redux/apis/vehicle/vehicleApi";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { AddVehicleModal } from "../AddVehicle/modals/AddVehicleModal";
import { DraftVehicleCard } from "./DraftVehicleCard";
import { MyVehiclesTable } from "./MyVehiclesTable";
import { useGetMyVehiclesParams } from "./hooks/useGetMyVehiclesParams";
import { ArchiveVehicleModal } from "./modals/ArchiveVehicleModal";

export const MyVehiclesPanel = () => {
  const { params, search, filters } = useGetMyVehiclesParams();

  // Main table query — only complete vehicles
  const {
    isFetching,
    data: response,
    error,
  } = useGetVehiclesQuery({ ...params, setup_incomplete: false }, {
    refetchOnMountOrArgChange: true,
  });

  // Separate query for draft vehicles — always on top, not paginated
  const { data: draftsResponse } = useGetVehiclesQuery(
    { owned_by: "PROVIDER", setup_incomplete: true, page_size: "100" },
    { refetchOnMountOrArgChange: true }
  );

  const [pendingArchive, setPendingArchive] = useState<Vehicle | undefined>();
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);

  const isFiltersEmpty = Object.values(filters).every((el) => el === undefined);
  const hasSearchOrFilters = search || !isFiltersEmpty;

  const draftVehicles = draftsResponse?.vehicles ?? [];
  const hasVehicles = (response && response.vehicles.length > 0) || draftVehicles.length > 0;

  if (error) return <ErrorPanel messages={getErrorMessages(error)} />;
  if (isFetching) return <Skeleton />;

  if (hasVehicles)
    return (
      <>
        {draftVehicles.length > 0 && (
          <div className="mb-4 flex flex-col gap-2">
            {draftVehicles.map((vehicle) => (
              <DraftVehicleCard key={vehicle.uuid} vehicle={vehicle} />
            ))}
          </div>
        )}
        {response && response.vehicles.length > 0 && (
          <>
            <MyVehiclesTable items={response.vehicles} setPendingArchive={setPendingArchive} />
            <MyVehiclesTable.Pagination page={response.page} />
          </>
        )}
        <ArchiveVehicleModal pendingArchive={pendingArchive} setPendingArchive={setPendingArchive} />
      </>
    );

  const getEmptyDescription = () => {
    if (hasSearchOrFilters) {
      return "There are no vehicles matching your filter criteria at the moment.";
    }
    return "You haven't added anything to this section. Get started by clicking the button below.";
  };

  return (
    <>
      <EmptyState title="Vehicle Manager" description={getEmptyDescription()}>
        {!hasSearchOrFilters && (
          <div className="flex flex-row gap-4">
            <Button variant="primary" startIcon="Add" size="md" onClick={() => setShowAddVehicleModal(true)}>
              Add Vehicle
            </Button>
          </div>
        )}
      </EmptyState>
      <AddVehicleModal open={showAddVehicleModal} onClose={() => setShowAddVehicleModal(false)} />
    </>
  );
};
